import { CircleAlert, LoaderCircle } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScaleMode } from "@/components/wide-block/scale-mode";
import { WideBlock } from "@/components/wide-block/WideBlock";
import { CodeView } from "@/conversation/artefacts/CodeView";
import { drawMermaid, drawnMermaid } from "@/conversation/artefacts/mermaid";
import { svgDataUrl } from "@/conversation/artefacts/svg";
import { cn } from "@/lib/utils";

/**
 * Drawings (artefacts-viewer design §4, #379, #380): an SVG sanitised and shown only as an image,
 * and a Mermaid diagram drawn in the sandboxed frame, then shown the same way. Used by the
 * artefact panel, the source viewer and Markdown (`mermaid` fences). Content is untrusted: none of
 * it is inserted as HTML in the app's document.
 */

/**
 * An SVG artefact: sanitised again here, then shown only as an image of a `data:` URL (no script,
 * no request, whatever it holds; opened on its own, an opaque origin), on the light diagram canvas.
 * Expand shows it full screen with zoom and pan (#379).
 */
export function SvgImage(props: {
  svg: string;
  title: string;
  /** The viewBox is in CSS px (Mermaid output), so Expand's 100 % is the drawing's own size. */
  natural?: boolean;
  /** The Mermaid source to show if the drawing does not survive sanitising. */
  fallback?: string;
  /** Shown instead of the default source view when the drawing does not survive sanitising. */
  failed?: ReactNode;
  /** The Expand button's name when "Expand <title>" reads badly. */
  expandLabel?: string;
}) {
  const url = useMemo(() => svgDataUrl(props.svg), [props.svg]);
  const size = useMemo(() => naturalSize(props.svg), [props.svg]);
  if (url === null) {
    if (props.failed !== undefined) {
      return props.failed;
    }
    return props.fallback !== undefined ? (
      <DiagramFailed code={<CodeView versionKey="" code={props.fallback} language="mermaid" />} />
    ) : (
      <DiagramFailed code={<CodeView versionKey="" code={props.svg} language="svg" />} />
    );
  }
  return (
    <DiagramBlock
      src={url}
      title={props.title}
      size={size}
      natural={props.natural === true && size !== null}
      expandLabel={props.expandLabel}
    />
  );
}

/**
 * A sanitised drawing (`data:` URL) on the light canvas, in a wide block, with Expand, zoom and
 * pan. With a known `size` it follows the person's Fit / Actual size choice (#379): shrunk to the
 * block's width (never enlarged) or at that size, scrolling. Without one it fills the column.
 */
export function DiagramBlock(props: {
  src: string;
  title: string;
  size: { width: number; height: number } | null;
  /** The size is in CSS px (Mermaid's viewBox): Expand's 100 % is that size, not the screen. */
  natural: boolean;
  expandLabel?: string | undefined;
}) {
  const { size } = props;
  const [mode, setMode] = useScaleMode();
  return (
    <WideBlock
      label={props.title}
      {...(props.expandLabel ? { expandLabel: props.expandLabel } : {})}
      expand="always"
      zoom={{
        src: props.src,
        alt: props.title,
        width: size?.width ?? null,
        height: size?.height ?? null,
        // Only Mermaid's viewBox is in CSS px; another SVG's is a ratio.
        fill: !props.natural,
      }}
      className="diagram-canvas rounded-lg border border-line"
      contentClassName="p-4"
      {...(size ? { scaleMode: mode, onScaleModeChange: setMode, naturalWidth: size.width } : {})}
    >
      {size ? (
        <img
          src={props.src}
          alt={props.title}
          width={size.width}
          height={size.height}
          className={cn("mx-auto block h-auto", mode === "fit" ? "max-w-full" : "max-w-none")}
          style={{ width: `${size.width}px` }}
        />
      ) : (
        <img src={props.src} alt={props.title} className="mx-auto block h-auto w-full" />
      )}
    </WideBlock>
  );
}

/** The size of an SVG's viewBox in CSS px (its drawn size, as Mermaid lays it out), if any. */
export function naturalSize(svg: string): { width: number; height: number } | null {
  const root = /<svg\b[^>]*>/i.exec(svg)?.[0] ?? "";
  const box = /\bviewBox\s*=\s*["']\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(root);
  const width = box?.[1] ? Number(box[1]) : Number.NaN;
  const height = box?.[2] ? Number(box[2]) : Number.NaN;
  return Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0
    ? { width: Math.ceil(width), height: Math.ceil(height) }
    : null;
}

/**
 * A Mermaid artefact, drawn in the sandboxed frame; the SVG it returns goes through the SVG path
 * above. A diagram that fails or takes more than 5 s shows its source with a plain message.
 */
export function MermaidDiagram(props: {
  /** The drawing cache's key: an artefact version, or the source's (`sourceKey`) for a fence. */
  cacheKey: string;
  source: string;
  title: string;
  /** Shown when the diagram cannot be drawn (Markdown: its own code block and note). */
  failed?: ReactNode;
  expandLabel?: string;
}) {
  const { t } = useTranslation();
  // A drawing already made (a remount, a view switch) shows at once, never the placeholder.
  const [state, setState] = useState<{ svg: string } | "drawing" | "failed">(() => {
    const known = drawnMermaid(props.cacheKey);
    return known === undefined ? "drawing" : { svg: known };
  });
  useEffect(() => {
    const known = drawnMermaid(props.cacheKey);
    if (known !== undefined) {
      setState({ svg: known });
      return;
    }
    const abort = new AbortController();
    // A new source keeps the last drawing on screen until its own is ready (no layout jump).
    setState((prev) => (typeof prev === "object" ? prev : "drawing"));
    drawMermaid(props.cacheKey, props.source, { signal: abort.signal }).then(
      (svg) => setState({ svg }),
      () => {
        if (!abort.signal.aborted) {
          setState("failed");
        }
      },
    );
    return () => abort.abort();
  }, [props.cacheKey, props.source]);
  if (state === "failed") {
    return (
      props.failed ?? (
        <DiagramFailed code={<CodeView versionKey="" code={props.source} language="mermaid" />} />
      )
    );
  }
  if (state === "drawing") {
    return (
      <DiagramCanvas>
        <p
          role="status"
          className="flex items-center justify-center gap-2 py-10 text-sm text-fg-muted"
        >
          <LoaderCircle
            aria-hidden="true"
            strokeWidth={1.75}
            className="size-4 animate-spin motion-reduce:animate-none"
          />
          {t("artefacts.diagram.drawing")}
        </p>
      </DiagramCanvas>
    );
  }
  return (
    <SvgImage
      svg={state.svg}
      title={props.title}
      natural
      fallback={props.source}
      failed={props.failed}
      expandLabel={props.expandLabel}
    />
  );
}

/**
 * Diagrams sit on a light, bordered canvas in both themes: model-written colours assume a light
 * ground (design §4, dark mode).
 */
/** The light canvas drawings sit on (they assume a white page). */
export function DiagramCanvas({ children }: { children: ReactNode }) {
  return (
    <div className="diagram-canvas overflow-auto rounded-lg border border-line p-4">{children}</div>
  );
}

/**
 * A drawing that could not be made: a short note (by default, that it could not be drawn), then
 * its source as code. Markdown passes its own code block and the too-large / too-many notes.
 */
export function DiagramFailed(props: { code: ReactNode; note?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <p className="flex items-start gap-2 text-sm text-fg-secondary" role="status">
        <CircleAlert
          aria-hidden="true"
          strokeWidth={1.75}
          className="mt-0.5 size-4 shrink-0 text-warning-fg"
        />
        {props.note ?? t("artefacts.diagram.failed")}
      </p>
      {props.code}
    </div>
  );
}
